/**
 * @file Hook de détection automatique des formules disponibles.
 * @module packages.common.components.table-reservation.hooks.useFormulaRequirement
 *
 * Après la sélection du service, de la date et du créneau horaire,
 * interroge l'API pour savoir si des formules sont disponibles.
 * Si oui, met à jour formData.formulasRequired et stocke les
 * formules dans formData.availableFormulas (et les formules complètes à
 * cette heure dans formData.unavailableFormulas, affichées grisées) pour
 * éviter un second appel API dans FormulaStep.
 */
import {useState, useEffect, useRef} from 'react';
import {useReservationData} from 'packages/common/components/table-reservation/context/ReservationDataContext';
import {logger} from 'packages/common/utils/clientLogger';

/**
 * Détecte automatiquement si des formules sont disponibles pour le contexte
 * sélectionné : service, date, nombre de convives et créneau. Le créneau et
 * les convives comptent : une formule à contraintes (bornes de convives,
 * salles imposées) n'est proposable, ou grisée parce que ses salles sont
 * pleines, que pour une heure précise. Les retirer des dépendances casserait
 * la liste et l'affichage grisé.
 *
 * Réagit aux changements de ce contexte et réinitialise l'état des formules,
 * puis interroge l'API pour pré-charger les formules dans formData.
 *
 * @param {Object} formData - Données du formulaire de réservation
 * @param {Function} updateFormData - Mutateur du formulaire (field, value)
 * @return {{ isChecking: boolean, hasChecked: boolean }}
 *   isChecking  - Vérification en cours auprès de l'API
 *   hasChecked  - Vérification terminée (succès ou échec)
 */
export function useFormulaRequirement(formData, updateFormData) {
  const {getAvailableFormulas} = useReservationData();
  const [isChecking, setIsChecking] = useState(false);
  const [hasChecked, setHasChecked] = useState(false);

  // Signature sérialisée des dépendances pour détecter les changements entre rendus
  const prevDepsRef = useRef(null);
  useEffect(() => {
    // L'heure et le nombre de convives font partie du contexte : une formule
    // à contraintes (bornes de convives, salle imposée) n'est proposable que
    // pour un créneau précis. Avant le choix de l'heure, le serveur n'applique
    // que les bornes, ce qui suffit à décider si l'étape existe.
    const currentDeps = `${formData.service}|${formData.date}|${formData.timeSlot || ''}|${formData.partySize || ''}`;
    const depsChanged = prevDepsRef.current !== null && prevDepsRef.current !== currentDeps;
    prevDepsRef.current = currentDeps;

    // Les formules précédentes ne sont plus valides si le contexte a changé
    if (depsChanged) {
      setHasChecked(false);
      updateFormData('formulasRequired', false);
      updateFormData('selectedFormulas', []);
      updateFormData('availableFormulas', []);
      updateFormData('unavailableFormulas', []);
    }

    // Service et date sont requis avant l'appel API
    if (!formData.service || !formData.date) {
      return;
    }

    const controller = new AbortController();
    let cancelled = false;

    const check = async () => {
      setIsChecking(true);

      try {
        // L'API attend un format YYYY-MM-DD strict
        const selectedDate = formData.date;
        let dateString;

        if (selectedDate instanceof Date) {
          const year = selectedDate.getFullYear();
          const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
          const day = String(selectedDate.getDate()).padStart(2, '0');
          dateString = `${year}-${month}-${day}`;
        } else if (typeof selectedDate === 'string') {
          dateString = selectedDate;
        } else {
          if (!cancelled) {
            updateFormData('formulasRequired', false);
          }
          return;
        }

        // Pré-charge les formules pour éviter un second appel dans FormulaStep.
        // L'horodatage du créneau prime sur l'heure texte : un créneau après
        // minuit appartient au lendemain de la date choisie au calendrier.
        const slotTimestamp = formData.selectedTimeSlotData?.timestamp;
        const {formulas, unavailableFormulas} = await getAvailableFormulas(
            {
              service: formData.service,
              date: dateString,
              partySize: formData.partySize || undefined,
              time: formData.timeSlot || undefined,
              dateTime: slotTimestamp ? new Date(slotTimestamp).toISOString() : undefined,
            },
            {signal: controller.signal},
        );

        if (cancelled) return;

        if (formulas.length > 0) {
          // Active l'étape FormulaStep et stocke les données pour éviter un second fetch.
          // Les formules complètes ne s'affichent qu'à côté d'au moins une formule
          // choisissable : jamais d'étape sans rien à choisir.
          updateFormData('formulasRequired', true);
          updateFormData('availableFormulas', formulas);
          updateFormData('unavailableFormulas', unavailableFormulas);
        } else {
          // Pas de formule : l'étape sera masquée dans le stepper
          updateFormData('formulasRequired', false);
          updateFormData('selectedFormulas', []);
          updateFormData('availableFormulas', []);
          updateFormData('unavailableFormulas', []);
        }

        setHasChecked(true);
      } catch (err) {
        if (cancelled || err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
        logger.error('Erreur lors de la vérification des formules:', err);
        // Dégradation gracieuse : le flow continue sans formules
        updateFormData('formulasRequired', false);
      } finally {
        if (!cancelled) {
          setIsChecking(false);
        }
      }
    };

    check();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [
    formData.service,
    formData.date,
    formData.timeSlot,
    formData.selectedTimeSlotData?.timestamp,
    formData.partySize,
    updateFormData,
    getAvailableFormulas,
  ]);

  return {
    isChecking,
    hasChecked,
  };
}
